import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Sidebar from "@/components/Sidebar";
import ChatPane from "@/components/ChatPane";
import WardrobeShelf from "@/components/WardrobeShelf";
import VoiceCallModal from "@/components/VoiceCallModal";
import VideoCallModal from "@/components/VideoCallModal";
import PaywallModal from "@/components/PaywallModal";
import PWABanner from "@/components/PWABanner";
import GiftCarousel from "@/components/GiftCarousel";
import MemoryBook from "@/components/MemoryBook";
import StreakCalendar from "@/components/StreakCalendar";
import AddressTrapModal from "@/components/AddressTrapModal";
import PricingPlans from "@/components/PricingPlans";
import AdLockModal from "@/components/AdLockModal";
import useAdLock from "@/hooks/useAdLock";
import Particles from "@/components/Particles";
import AdBlock from "@/components/AdBlock";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Heart } from "lucide-react";
import { toast } from "sonner";

const IDLE_MS = 90_000;

export default function Dashboard() {
  const nav = useNavigate();
  const { user, upgrade, setPanel } = useAuth();
  const [burst, setBurst] = useState(false);
  const [characters, setCharacters] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const [mobileNav, setMobileNav] = useState(false);
  const [wardrobe, setWardrobe] = useState(false);
  const [voice, setVoice] = useState(false);
  const [video, setVideo] = useState(false);
  const [paywall, setPaywall] = useState(null);
  const [gift, setGift] = useState(false);
  const [memory, setMemory] = useState(false);
  const [streak, setStreak] = useState(false);
  const [pricing, setPricing] = useState(false);
  const [addrTrap, setAddrTrap] = useState(false);
  const { adLocked, setAdLocked } = useAdLock();

  const idleTimer = useRef(null);
  const proactiveSent = useRef(false);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const addrShownRef = useRef(false);
  const isPremium = user?.tier === "premium";
  const isVip = user?.plan === "vip";

  const loadCharacters = useCallback(async () => {
    const { data } = await api.get("/characters");
    setCharacters(data);
    setLoading(false);
    if (data.length === 0) { nav("/onboarding"); return; }
    setActive((prev) => data.find((c) => c.id === prev?.id) || data[0]);
  }, [nav]);

  useEffect(() => { loadCharacters(); }, [loadCharacters]);

  useEffect(() => {
    if (!active) return;
    proactiveSent.current = false;
    api.get(`/chat/${active.id}`).then(({ data }) => setMessages(data));
    if ("Notification" in window && Notification.permission === "default") {
      setTimeout(() => Notification.requestPermission().catch(() => {}), 1200);
    }
  }, [active?.id]);

  const resetIdle = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (!active || proactiveSent.current) return;
    idleTimer.current = setTimeout(async () => {
      try {
        proactiveSent.current = true;
        const { data } = await api.post("/chat/proactive", { character_id: active.id });
        setMessages((m) => [...m, data.message]);
        if ("Notification" in window && Notification.permission === "granted")
          new Notification(active.name, { body: data.message.content, icon: "/icon.svg" });
        else toast(`${active.name}: ${data.message.content}`);
      } catch {}
    }, IDLE_MS);
  }, [active?.id]);

  useEffect(() => { resetIdle(); return () => idleTimer.current && clearTimeout(idleTimer.current); }, [resetIdle, messages]);

  const send = async (retryMsg) => {
    const text = retryMsg ? retryMsg.content : draft.trim();
    if (!text || sending) return;
    if (adLocked) return setAdLocked(true);
    const tmpId = `tmp-${Date.now()}`;
    if (retryMsg) setMessages((m) => m.map((x) => x.id === retryMsg.id ? { ...x, id: tmpId, failed: false } : x));
    else { setDraft(""); setMessages((m) => [...m, { id: tmpId, sender: "user", content: text }]); }
    proactiveSent.current = false;
    setSending(true);
    try {
      const { data } = await api.post("/chat", { character_id: active.id, content: text }, { timeout: 70000 });
      // Reload authoritative history from DB to avoid any stale/duplicated bubbles.
      const { data: hist } = await api.get(`/chat/${active.id}`);
      setMessages(hist);
      setActive((a) => ({ ...a, trust_score: data.trust_score }));
      setCharacters((cs) => cs.map((c) => c.id === active.id ? { ...c, trust_score: data.trust_score } : c));
      if (data.address_trap && !data.ad_locked && !addrShownRef.current) { addrShownRef.current = true; setAddrTrap(true); }
      if (data.ad_locked) setAdLocked(true);
    } catch (e) {
      setMessages((m) => m.map((x) => x.id === tmpId ? { ...x, failed: true } : x));
      if (e.response?.status === 423) { setAdLocked(true); return; }
      toast.error(e.response?.data?.detail || "Message failed to send. Tap retry.");
    }
    finally { setSending(false); }
  };

  const applyOutfit = async (url) => {
    const { data } = await api.patch(`/characters/${active.id}`, { active_outfit_url: url });
    setActive(data);
    setCharacters((cs) => cs.map((c) => c.id === data.id ? data : c));
    setWardrobe(false);
    toast.success("Outfit changed");
  };

  const doUpgrade = async () => { await upgrade(); setPaywall(null); toast.success("Premium unlocked ✨"); };

  const handleSelfie = async () => {
    if (!isPremium) {
      setPaywall({ title: "On-Demand Selfie Engine",
        description: "Upgrade to Premium to receive exclusive, ultra-realistic custom photos of your companion tailored to your conversation — instantly.",
        perks: ["Custom-generated selfies", "Scenario-aware photos", "Unlimited requests"] });
      return;
    }
    toast.loading("Generating selfie…", { id: "selfie" });
    try {
      const { data } = await api.post("/selfie", { character_id: active.id });
      setMessages((m) => [...m, { id: `selfie-${Date.now()}`, sender: "ai", content: "", image: data.image_url }]);
      toast.success("📸 Selfie received", { id: "selfie" });
    } catch { toast.error("Selfie service unavailable (keys untested)", { id: "selfie" }); }
  };

  const giftReaction = (data) => {
    setMessages((m) => [...m, data.message]);
    setActive((a) => ({ ...a, trust_score: data.trust_score }));
    setGift(false);
    setBurst(true);
    setTimeout(() => setBurst(false), 1200);
  };

  const selectChar = (c) => { setActive(c); setMobileNav(false); };

  const sidebar = (
    <Sidebar characters={characters} active={active} onSelect={selectChar}
      onNewCharacter={() => nav("/onboarding")}
      onOpenWardrobe={() => { setWardrobe(true); setMobileNav(false); }}
      onMemory={() => { setMemory(true); setMobileNav(false); }}
      onStreak={() => { setStreak(true); setMobileNav(false); }}
      onPricing={() => { setPricing(true); setMobileNav(false); }}
      onPortal={() => nav("/portal")}
      onBlindDate={async () => { try { await setPanel("blinddate"); } catch {} nav("/blinddate"); }} />
  );

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Heart className="w-7 h-7 text-brand animate-pulse" fill="currentColor" /></div>;

  return (
    <div className="h-[100dvh] flex overflow-hidden bg-background" onMouseMove={resetIdle} onKeyDown={resetIdle}>
      {isDesktop && <div className="w-[320px] shrink-0">{sidebar}</div>}
      <Sheet open={mobileNav && !isDesktop} onOpenChange={setMobileNav}>
        <SheetContent side="left" className="p-0 w-[320px] border-white/10">
          <SheetTitle className="sr-only">Navigation</SheetTitle>{sidebar}
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex flex-col min-w-0 relative">
        <PWABanner />
        {/* top rail ad */}
        {!isPremium && <AdBlock id="ad-slot-top-rail" size="728x90" desktopOnly className="h-[90px] rounded-none border-x-0 border-t-0" />}
        <div className="flex-1 min-h-0">
          <ChatPane character={active} messages={messages} sending={sending} draft={draft} setDraft={setDraft} onSend={() => send()} onRetry={send}
            onVoice={() => setVoice(true)} onVideo={() => setVideo(true)} onSelfie={handleSelfie}
            onWardrobe={() => setWardrobe(true)} onGift={() => setGift(true)} onMenu={() => setMobileNav(true)} variant="hub" />
        </div>
        <Particles show={burst} />
      </div>

      <WardrobeShelf open={wardrobe} onClose={() => setWardrobe(false)} character={active} onApply={applyOutfit} />
      <VoiceCallModal open={voice} onClose={() => setVoice(false)} character={active} locked={!isPremium} onUpgrade={doUpgrade} />
      <VideoCallModal open={video} onClose={() => setVideo(false)} character={active} locked={!isVip} onUpgrade={() => { setVideo(false); setPricing(true); }} />
      <PaywallModal open={!!paywall} onClose={() => setPaywall(null)} onUpgrade={doUpgrade} title={paywall?.title} description={paywall?.description} perks={paywall?.perks || []} />
      {active && <GiftCarousel open={gift} onClose={() => setGift(false)} character={active} onReaction={giftReaction} />}
      {active && <MemoryBook open={memory} onClose={() => setMemory(false)} character={active} />}
      <StreakCalendar open={streak} onClose={() => setStreak(false)} />
      <PricingPlans open={pricing} onClose={() => setPricing(false)} />
      {active && <AddressTrapModal open={addrTrap && !adLocked} onClose={() => setAddrTrap(false)} character={active} />}
      <AdLockModal open={adLocked} onUnlocked={() => setAdLocked(false)} />
    </div>
  );
}
